/**
 * Per-role tab + sidebar visibility hooks for the web customer app.
 * Mirrors mobile's `useVisibleProjectTabs` / `useVisibleBottomTabs`
 * by sourcing the per-role maps from `@shared/perms/visibleTabs`,
 * so an Accountant on web sees the same 2 project-detail tabs they
 * see on mobile (Transactions + Party), not all 9.
 *
 * Three hooks:
 *   - `useVisibleProjectTabs()` — drives the project-detail tab strip
 *     in `routes/ProjectDetail.tsx`. Returns a `Set<ProjectTabKey>`.
 *   - `useVisibleSidebar()` — drives the sidebar nav in `App.tsx`.
 *     Returns a `Set<SidebarNavKey>` so each nav group can be
 *     filtered via `set.has(key)`.
 *   - `useVisibleBottomTabs()` — not used on web today; exposed for
 *     symmetry in case we ever ship a mobile-web view.
 *
 * Loading policy mirrors mobile: while role is settling, return the
 * EMPTY set rather than the full set, so a brief flash never reveals
 * a tab the role can't see.
 */
import { useMemo } from 'react';

import {
  visibleBottomTabs,
  visibleProjectTabs,
  visibleSidebar,
  EMPTY_BOTTOM_TABS,
  EMPTY_PROJECT_TABS,
  EMPTY_SIDEBAR,
  type BottomTabKey,
  type ProjectTabKey,
  type SidebarNavKey,
} from '@shared/perms';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';

export type { BottomTabKey, ProjectTabKey, SidebarNavKey };

export function useVisibleProjectTabs(): ReadonlySet<ProjectTabKey> {
  const { role, loading } = useCurrentOrgRole();
  return useMemo(() => {
    if (loading || !role) return EMPTY_PROJECT_TABS;
    return visibleProjectTabs(role);
  }, [role, loading]);
}

export function useVisibleSidebar(): ReadonlySet<SidebarNavKey> {
  const { role, loading } = useCurrentOrgRole();
  return useMemo(() => {
    if (loading || !role) return EMPTY_SIDEBAR;
    return visibleSidebar(role);
  }, [role, loading]);
}

export function useVisibleBottomTabs(): ReadonlySet<BottomTabKey> {
  const { role, loading } = useCurrentOrgRole();
  return useMemo(() => {
    if (loading || !role) return EMPTY_BOTTOM_TABS;
    return visibleBottomTabs(role);
  }, [role, loading]);
}
